import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Localized, useLocalization } from "@fluent/react";
import {
    Alert,
	Box,
	Button,
	Checkbox,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMemo, useState } from "react";
import { CancelButton } from "../../../../app/components/CancelButton";
import { SaveButton } from "../../../../app/components/SaveButton";
import { DOC_SECTION_TYPES, DocSectionType, DocumentTypes } from "../../../documents/documentTypes";
import { DocumentSectionMapping, syncDocumentSectionMapping } from "../documentSectionMappingsApi";
import { showSuccess } from "../../../notifications/notificationsSlice";
import { useAppDispatch } from "../../../../app/hooks";

export interface DocumentSectionMappingDraft {
	documentType: DocumentTypes;
	sectionType: DocSectionType;
	singleInDocument: boolean;
	position: number;
}

interface RightItem {
	sectionType: DocSectionType;
	singleInDocument: boolean;
	checked: boolean;
}

const RIGHT_PANEL_GRID_COLUMNS = "32px 42px 32px 1fr 70px";

interface DocumentSectionMappingsTransferDialogProps {
	documentType: DocumentTypes;
	mappings: DocumentSectionMapping[];
	onCancel: () => void;
	onSuccess: (mappings: DocumentSectionMapping[]) => void;
}

const SortableRightItem = ({
	item,
	position,
	onToggleChecked,
	onToggleSingleInDocument,
}: {
	item: RightItem;
	position: number;
	onToggleChecked: () => void;
	onToggleSingleInDocument: () => void;
}) => {
	const { l10n } = useLocalization();
	const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id: item.sectionType });

	return (
		<ListItem
			ref={setNodeRef}
			divider
			sx={{
				display: "grid",
				gridTemplateColumns: RIGHT_PANEL_GRID_COLUMNS,
				alignItems: "center",
				columnGap: 1,
				paddingRight: 2,
				transform: CSS.Transform.toString(transform),
				transition,
				backgroundColor: isDragging ? "action.hover" : undefined,
			}}
		>
			<ListItemIcon sx={{ minWidth: 32, cursor: "grab", touchAction: "none" }} {...attributes} {...listeners}>
				<DragIndicatorIcon fontSize="small" color="action" />
			</ListItemIcon>
			<Checkbox edge="start" checked={item.checked} onChange={onToggleChecked} tabIndex={-1} />
			<ListItemText primary={position} />
			<ListItemText primary={item.sectionType} />
			<Tooltip title={l10n.getString("document-section-mappings-single-in-document", null, "Tylko jedna sekcja w dokumencie")}>
				<Checkbox checked={item.singleInDocument} onChange={onToggleSingleInDocument} sx={{ justifySelf: "start" }} />
			</Tooltip>
		</ListItem>
	);
};

export const DocumentSectionMappingsTransferDialog = ({
	documentType,
	mappings,
	onCancel,
	onSuccess,
}: DocumentSectionMappingsTransferDialogProps) => {
    const dispatch = useAppDispatch();

    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);

	const initialRightItems = useMemo<RightItem[]>(
		() =>
			mappings
				.filter((mapping) => mapping.documentType === documentType)
				.slice()
				.sort((left, right) => (left.position ?? Number.MAX_SAFE_INTEGER) - (right.position ?? Number.MAX_SAFE_INTEGER))
				.map((mapping) => ({
					sectionType: mapping.sectionType as DocSectionType,
					singleInDocument: mapping.singleInDocument,
					checked: false,
				})),
		[documentType, mappings]
	);

	const [rightItems, setRightItems] = useState<RightItem[]>(initialRightItems);
	const [leftChecked, setLeftChecked] = useState<Set<DocSectionType>>(new Set());

	const leftItems = useMemo(
		() => DOC_SECTION_TYPES.filter((sectionType) => !rightItems.some((item) => item.sectionType === sectionType)),
		[rightItems]
	);

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

	const toggleLeftChecked = (sectionType: DocSectionType) => {
		setLeftChecked((previous) => {
			const next = new Set(previous);
			if (next.has(sectionType)) {
				next.delete(sectionType);
			} else {
				next.add(sectionType);
			}
			return next;
		});
	};

	const toggleRightChecked = (sectionType: DocSectionType) => {
		setRightItems((previous) =>
			previous.map((item) => (item.sectionType === sectionType ? { ...item, checked: !item.checked } : item))
		);
	};

	const toggleSingleInDocument = (sectionType: DocSectionType) => {
		setRightItems((previous) =>
			previous.map((item) => (item.sectionType === sectionType ? { ...item, singleInDocument: !item.singleInDocument } : item))
		);
	};

	const moveToRight = () => {
		const toMove = leftItems.filter((sectionType) => leftChecked.has(sectionType));
		if (toMove.length === 0) {
			return;
		}

		setRightItems((previous) => [
			...previous,
			...toMove.map((sectionType) => ({ sectionType, singleInDocument: false, checked: false })),
		]);
		setLeftChecked(new Set());
	};

	const moveToLeft = () => {
		setRightItems((previous) => previous.filter((item) => !item.checked));
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) {
			return;
		}

		setRightItems((previous) => {
			const oldIndex = previous.findIndex((item) => item.sectionType === active.id);
			const newIndex = previous.findIndex((item) => item.sectionType === over.id);
			if (oldIndex === -1 || newIndex === -1) {
				return previous;
			}

			return arrayMove(previous, oldIndex, newIndex);
		});
	};

	const handleSave = async () => {
		const payload: DocumentSectionMappingDraft[] = rightItems.map((item, index) => ({
			documentType,
			sectionType: item.sectionType,
			singleInDocument: item.singleInDocument,
			position: index + 1,
		}));

        setSaving(true);
        try {
            const result = await dispatch(syncDocumentSectionMapping({
                documentType,
                mappings: payload
            }));

            if (syncDocumentSectionMapping.fulfilled.match(result)) {
                dispatch(showSuccess("saved"));
                onSuccess(result.payload);
            } else {
                let errorMsg = "error";
                if (result.payload && result.payload.kind === "http" && result.payload.problem) {
                    errorMsg = result.payload.problem.title;
                }

                setServerError(errorMsg);
            }
        } catch {
            setServerError("error");
        } finally {
            setSaving(false);
        }
	};

	return (
		<>
			<DialogTitle>{documentType}</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
				<Grid container padding={2} spacing={2} sx={{ flex: 1, minHeight: 0 }}>
					<Grid item xs={5} sx={{ height: "100%" }}>
						<Paper variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
							<Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider" }}>
								<Typography variant="subtitle2">
									<Localized id="document-section-mappings-available">_Dostępne</Localized>
								</Typography>
							</Box>
							<Box sx={{ display: "flex", alignItems: "center", px: 1, py: 0.5, borderBottom: 1, borderColor: "divider", backgroundColor: "action.hover" }}>
								<Box sx={{ width: 36 }} />
								<Typography variant="caption" color="text.secondary" fontWeight={600}>
									<Localized id="doc-section-type">_Typ sekcji</Localized>
								</Typography>
							</Box>
							<List dense sx={{ overflowY: "auto", flex: 1 }}>
								{leftItems.map((sectionType) => (
									<ListItem key={sectionType} disablePadding>
										<ListItemButton onClick={() => toggleLeftChecked(sectionType)}>
											<ListItemIcon sx={{ minWidth: 36 }}>
												<Checkbox edge="start" checked={leftChecked.has(sectionType)} tabIndex={-1} disableRipple />
											</ListItemIcon>
											<ListItemText primary={sectionType} />
										</ListItemButton>
									</ListItem>
								))}
							</List>
						</Paper>
					</Grid>
					<Grid item xs={2} sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1 }}>
						<Button variant="outlined" size="small" onClick={moveToRight} disabled={leftChecked.size === 0}>
							<ArrowForwardIcon />
						</Button>
						<Button variant="outlined" size="small" onClick={moveToLeft} disabled={!rightItems.some((item) => item.checked)}>
							<ArrowBackIcon />
						</Button>
					</Grid>
					<Grid item xs={5} sx={{ height: "100%" }}>
						<Paper variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
							<Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider" }}>
								<Typography variant="subtitle2">
									<Localized id="document-section-mappings-selected">_Wybrane</Localized>
								</Typography>
							</Box>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: RIGHT_PANEL_GRID_COLUMNS,
									alignItems: "center",
									columnGap: 1,
									px: 1,
									paddingRight: 2,
									py: 0.5,
									borderBottom: 1,
									borderColor: "divider",
									backgroundColor: "action.hover",
								}}
							>
								<Box />
								<Box />
								<Typography variant="caption" color="text.secondary" fontWeight={600}>
									<Localized id="document-section-mappings-column-position">_Lp.</Localized>
								</Typography>
								<Typography variant="caption" color="text.secondary" fontWeight={600}>
									<Localized id="doc-section-type">_Typ sekcji</Localized>
								</Typography>
								<Typography variant="caption" color="text.secondary" fontWeight={600}>
									<Localized id="document-section-mappings-column-unique">_Unikalna</Localized>
								</Typography>
							</Box>
							<Box sx={{ overflowY: "auto", flex: 1 }}>
								<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
									<SortableContext items={rightItems.map((item) => item.sectionType)} strategy={verticalListSortingStrategy}>
										<List dense disablePadding>
											{rightItems.map((item, index) => (
												<SortableRightItem
													key={item.sectionType}
													item={item}
													position={index + 1}
													onToggleChecked={() => toggleRightChecked(item.sectionType)}
													onToggleSingleInDocument={() => toggleSingleInDocument(item.sectionType)}
												/>
											))}
										</List>
									</SortableContext>
								</DndContext>
							</Box>
						</Paper>
					</Grid>
				</Grid>

                {
                    serverError && <Alert sx={{ marginTop: 1, }} onClose={() => { setServerError("") }} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                }
			</DialogContent>
			<DialogActions>
				<SaveButton loading={saving} onClick={handleSave} />
				<CancelButton loading={saving} onClick={onCancel} />
			</DialogActions>
		</>
	);
};
